/**
 * How the browser sends its tools to the chat model (CACHE step 2,
 * 2026-10-19). The server's TOOL_DELIVERY setting, which the client learns
 * from /route (companion-api tool-delivery.ts), one of:
 *
 *  - legacy: the page's core tools, then what get_tools loaded this turn, at
 *            the end. A load changes the list, and the provider's cached
 *            prompt prefix (the tool list comes first) is lost for that round.
 *  - fixed:  one list on every round: the core, with get_tools saying how to
 *            call what it loads, then `use_tool`. A loaded tool's schema rides
 *            in get_tools' result (after the cached prefix), and the model
 *            calls it through use_tool, which the manager turns back into the
 *            tool's own call (checked against its schema) before the host runs it.
 *
 * Which tools a turn may use (the core, the index, the access gate) is the
 * same on both; only how they reach the model differs.
 */
import type { ToolCall, ToolDefinition, ToolResult } from '../types/api'
import { TOOL_DELIVERIES, type ToolDelivery } from './tool-delivery-mode'
import { GET_TOOLS_TOOL } from './tool-schemas'
import { validateArgs } from './validate-args'

export const USE_TOOL = 'use_tool'

/** The setting as /route sends it; null for anything else. */
export function parseToolDelivery(value: unknown): ToolDelivery | null {
  return TOOL_DELIVERIES.includes(value as ToolDelivery) ? (value as ToolDelivery) : null
}

/** The fixed delivery's generic entry: the one way to call a tool get_tools loaded. */
export const USE_TOOL_TOOL: ToolDefinition = {
  name: USE_TOOL,
  description:
    'Call a tool that get_tools loaded this turn: those tools are not in your tool list. name is the tool; args its arguments, as the schema get_tools returned says.',
  input_schema: {
    type: 'object',
    properties: {
      name: { type: 'string', description: 'The tool to call (one get_tools loaded)' },
      args: { type: 'object', description: "The tool's arguments, as its schema says" },
    },
    required: ['name'],
  },
}

/** get_tools as the fixed delivery offers it: what it loads is called through use_tool. */
export const GET_TOOLS_FIXED_TOOL: ToolDefinition = {
  ...GET_TOOLS_TOOL,
  description: `${GET_TOOLS_TOOL.description} A tool it loads is not added to your tool list: call it through ${USE_TOOL}, with its name and args.`,
}

/** The line a fixed delivery adds to get_tools' result, right after the schemas. */
export const LOADED_THROUGH_USE_TOOL = `These are not in your tool list: call each through ${USE_TOOL}, with its name and args.`

/**
 * The fixed delivery's tool list, the same on every round: the page's
 * always-loaded tools (`core`), get_tools saying how to call what it loads,
 * then use_tool.
 */
export function fixedRoundTools(core: readonly ToolDefinition[]): ToolDefinition[] {
  return [
    ...core.map((t) => (t.name === GET_TOOLS_TOOL.name ? GET_TOOLS_FIXED_TOOL : t)),
    USE_TOOL_TOOL,
  ]
}

/** A use_tool call's arguments: an object, or one the model wrote as a JSON string. */
function argsOf(args: unknown): Record<string, unknown> | null {
  if (args == null) return {}
  if (typeof args === 'string') {
    try {
      const parsed: unknown = JSON.parse(args)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null
    } catch {
      return null
    }
  }
  return typeof args === 'object' && !Array.isArray(args) ? (args as Record<string, unknown>) : null
}

/**
 * A use_tool call refused before the host: the tool it named (use_tool itself
 * when it named none), and the result the model reads back.
 */
export interface RefusedCall {
  name: string
  result: ToolResult
}

const refused = (call: ToolCall, name: string, content: string): RefusedCall => ({
  name,
  result: { tool_use_id: call.tool_use_id, success: false, is_error: true, content },
})

/**
 * A round's calls as the host runs them: each use_tool call becomes the call
 * of the tool it names, with the model's call id, when that tool is one this
 * turn offers (`callable`: the core and what get_tools loaded) and its args
 * match the tool's schema. Any other use_tool call is refused with what to fix
 * and never reaches the host. Other calls pass as they are.
 */
export function unwrapUseTool(
  calls: readonly ToolCall[],
  callable: readonly ToolDefinition[],
): { calls: ToolCall[]; rejected: RefusedCall[] } {
  const out: ToolCall[] = []
  const rejected: RefusedCall[] = []
  for (const call of calls) {
    if (call.name !== USE_TOOL) {
      out.push(call)
      continue
    }
    const name = typeof call.input.name === 'string' ? call.input.name : ''
    const def = name && name !== USE_TOOL ? callable.find((t) => t.name === name) : undefined
    if (!name) {
      rejected.push(refused(call, USE_TOOL, `${USE_TOOL} needs name: the tool to call.`))
      continue
    }
    if (!def) {
      rejected.push(
        refused(
          call,
          name,
          `${name} is not loaded this turn: call get_tools with ["${name}"] first (it must be in the additional tools list), then call it through ${USE_TOOL}.`,
        ),
      )
      continue
    }
    const args = argsOf(call.input.args)
    const issues = args === null ? ['args: expected object'] : validateArgs(def.input_schema, args)
    if (issues.length > 0) {
      rejected.push(
        refused(
          call,
          name,
          `The arguments for ${name} do not match its schema: ${issues.join('; ')}. Its schema: ${JSON.stringify(def.input_schema)}`,
        ),
      )
      continue
    }
    out.push({ tool_use_id: call.tool_use_id, name, input: args ?? {} })
  }
  return { calls: out, rejected }
}
